import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type ApiErrorDetail, type Voice } from "../../api/client";
import { navigateTo, pathForTab } from "../../app/tabRoutes";
import { readCsrfCookie } from "../auth/csrfCookie";
import { applyVoice, voicesUsed } from "./templateVoice";
import type { FlowDocument } from "./types";

interface SaveTemplatePanelProps {
  document: FlowDocument;
  /** Whether "Place test call" is offered — tenant_admin only, like the page it opens. */
  canPlaceTestCall: boolean;
  /**
   * Set when the designer was opened on an existing template: saving then
   * publishes that template's NEXT version instead of starting a new
   * template. The published version itself is still never modified.
   */
  template?: { flowId: string; name: string; nextVersion: number };
  onClose: () => void;
}

type SaveState =
  | { status: "editing" }
  | { status: "saving" }
  | { status: "saved"; templateId: string; name: string; versionNumber: number }
  | { status: "error"; message: string; details: ApiErrorDetail[] };

const KEEP_VOICES = "";

function describeError(error: unknown): { message: string; details: ApiErrorDetail[] } {
  if (error instanceof ApiError) return { message: error.message, details: error.body.details ?? [] };
  if (error instanceof Error) return { message: error.message, details: [] };
  return { message: "could not save the template", details: [] };
}

/**
 * Saves the designer's current document as a template: a published,
 * immutable flow version (POST /v1/flows) whose server-generated UUID is
 * the template ID — the same `flow_version_id` that Test call, the
 * Templates page and the API's `template-id` all take. Nothing new on the
 * server: a template IS a published flow version with a name.
 *
 * Offers one registered voice for every spoken prompt (templateVoice.ts),
 * pre-selected when the flow uses a voice the tenant hasn't registered, so
 * a shipped sample can be saved without editing each node by hand.
 */
export function SaveTemplatePanel({ document, canPlaceTestCall, template, onClose }: SaveTemplatePanelProps) {
  const [name, setName] = useState(template?.name ?? document.id);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [voiceKey, setVoiceKey] = useState(KEEP_VOICES);
  const [state, setState] = useState<SaveState>({ status: "editing" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listVoices()
      .then(({ voices: registered }) => {
        if (cancelled) return;
        setVoices(registered);
        const known = new Set(registered.map((v) => `${v.voice}\u0000${v.language}`));
        const unregistered = voicesUsed(document).some((v) => !known.has(`${v.voice}\u0000${v.language}`));
        const first = registered[0];
        if (unregistered && first) setVoiceKey(`${first.voice}\u0000${first.language}`);
      })
      .catch(() => {
        // The voice picker is a convenience; saving still works without it
        // and reports an unregistered voice the same way publish always has.
      });
    return () => {
      cancelled = true;
    };
  }, [document]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ status: "saving" });
    const [voice, language] = voiceKey.split("\u0000");
    const program = voiceKey && voice && language ? applyVoice(document, voice, language) : document;
    try {
      const published = await api.publishFlow(
        readCsrfCookie(window.document.cookie),
        name.trim(),
        program,
        template ? { flowId: template.flowId } : undefined,
      );
      setState({
        status: "saved",
        templateId: published.flow_version_id,
        name: name.trim(),
        versionNumber: published.version_number,
      });
    } catch (err) {
      setState({ status: "error", ...describeError(err) });
    }
  }

  async function copyId(templateId: string) {
    try {
      await navigator.clipboard.writeText(templateId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked: the id is still shown and selectable.
    }
  }

  if (state.status === "saved") {
    return (
      <div className="save-template save-template--done" role="status">
        <div>
          <p className="save-template__title">
          Saved &ldquo;{state.name}&rdquo; as v{state.versionNumber}
        </p>
          <p className="save-template__id">
            Template ID <code>{state.templateId}</code>{" "}
            <button type="button" className="btn--outline btn--sm" onClick={() => void copyId(state.templateId)}>
              {copied ? "Copied" : "Copy"}
            </button>
          </p>
        </div>
        <div className="save-template__actions">
          {canPlaceTestCall ? (
            <button
              type="button"
              onClick={() => navigateTo(`${pathForTab("test-call")}?template=${encodeURIComponent(state.templateId)}`)}
            >
              Place test call
            </button>
          ) : null}
          <button type="button" className="btn--outline" onClick={() => navigateTo(pathForTab("flows-list"))}>
            View templates
          </button>
          <button type="button" className="btn--outline" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="save-template" onSubmit={(event) => void save(event)}>
      <p className="save-template__title">Save as template</p>
      <div className="save-template__fields">
        <label className="field">
          <span>Template name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} required maxLength={200} autoFocus />
        </label>
        <label className="field">
          <span>Voice for every spoken prompt</span>
          <select value={voiceKey} onChange={(event) => setVoiceKey(event.target.value)}>
            <option value={KEEP_VOICES}>Keep each node&rsquo;s own voice</option>
            {voices.map((v) => (
              <option key={`${v.voice}\u0000${v.language}`} value={`${v.voice}\u0000${v.language}`}>
                {v.voice} ({v.language})
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="card__note">
        Saving publishes an unchangeable version of this flow. Its template ID is what Test call
        and the API&rsquo;s <code>template-id</code> use.
      </p>
      {state.status === "error" ? (
        <div className="card__error" role="alert">
          <p>{state.message}</p>
          {state.details.length > 0 ? (
            <ul>
              {state.details.map((d, i) => (
                <li key={i}>
                  {d.node_id ? <code>{d.node_id}</code> : null} {d.detail ?? d.code}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      <div className="save-template__actions">
        <button type="submit" disabled={state.status === "saving" || name.trim() === ""}>
          {state.status === "saving" ? "Saving…" : "Save template"}
        </button>
        <button type="button" className="btn--outline" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
